// applyTileGridLayout — IMPERATIVE owner of the in-call TileGrid layout.
//
// Why imperative: v3.14 gives the grid no reactive avenue at all —
//   1. reactive gridTemplateColumns/Rows factories FREEZE at mount value
//      (the original cut-off bug: mounted at 1 participant, later joiners
//      stacked into overflow rows under the ControlBar);
//   2. design tokens don't resolve inside composite grid strings, so a
//      static `repeat(auto-fit, minmax(F1, 1fr))` emitted invalid CSS and
//      collapsed to ONE column (the 9px-sliver regression, 2026-06-12);
//   3. conditional `class:` objects only re-evaluate when the element
//      itself re-renders — TileGrid persists for the whole call, so a
//      mode class toggled off root state never updates either.
//
// So the same connectCall events that mirror LiveKit state into root
// (participants sync, active-speaker refresh) call this to write the
// templates straight onto the stable node — the established meet pattern
// (connectCall already drives per-tile <video> attachment by data-tile-id
// for the same v3.14 reasons). rAF defers the write past the smbls render
// pass so freshly added tiles exist before the featured-duplicate pass.
//
// Modes (mirrors the old factory breakpoints):
//   featured (screen-share / speaker / host on stage) → filmstrip: one
//     horizontal row, scrolls sideways when crowded; the featured
//     participant's own strip tile is display:none'd — it was rendering
//     as a duplicate "spare box" above the strip (2026-06-12 per Nika).
//   no featured → N×M grid sharing the height (2/3/4 columns by count).
import { resolveFeaturedParticipant } from './meetUtils.js'
import { attachFeaturedVideo } from './attachTracks.js'
import { paginateTiles } from './tilePaging.js'

export const applyTileGridLayout = (root, livekitRoom = null) => {
  if (typeof document === 'undefined' || typeof requestAnimationFrame === 'undefined') return
  requestAnimationFrame(() => {
    const node = document.querySelector('[data-key="TileGrid"]')
    if (!node) return
    const list = root?.livekitParticipants || []
    // #2224 — the grid's column/row math tracks what's actually RENDERED
    // (the current page under a tiles-per-screen cap), not the raw
    // participant count — otherwise a capped call would keep reserving grid
    // cells for tiles that paginated away, leaving empty gaps.
    const visible = paginateTiles(list, root?._meetTilePage, root?._meetTilesPerScreen)
    const n = visible.length || 1
    const featured = resolveFeaturedParticipant({ root })
    const st = node.style
    if (featured) {
      // FIXED tile size in the strip (2026-06-12 per Nika "maybe we need
      // to make grid tiles fixed") — stretchy 1fr tiles resized on every
      // join/leave; fixed 16:9 boxes keep the strip visually stable and
      // simply scroll sideways as people join.
      st.gridAutoFlow = 'column'
      st.gridAutoColumns = '224px'
      st.gridTemplateColumns = 'none'
      // Row fills the 45%-basis strip (fixed 126px left dead space below
      // the tiles once the strip went flex-basis); column width stays
      // FIXED so the strip is stable across joins/leaves.
      st.gridTemplateRows = 'minmax(0, 1fr)'
      st.justifyContent = 'safe center'
      st.overflowX = 'auto'
      st.overflowY = 'hidden'
    } else {
      const cols = n <= 1 ? 1 : n <= 4 ? 2 : n <= 9 ? 3 : 4
      const rows = Math.max(1, Math.ceil(n / cols))
      st.gridAutoFlow = ''
      st.gridAutoColumns = ''
      st.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`
      st.gridTemplateRows = `repeat(${rows}, minmax(0, 1fr))`
      st.justifyContent = ''
      st.overflowX = ''
      st.overflowY = ''
    }
    // Hide the featured participant's duplicate strip tile (CSS-only —
    // the element stays mounted so its tracks aren't torn down; audio
    // keeps playing through the featured tile's attachment).
    const featuredId = featured ? featured.id || featured.identity : null
    for (const child of node.children) {
      if (!child.getAttribute) continue
      const tid = child.getAttribute('data-tile-id')
      if (!tid) continue
      child.style.display = featuredId && tid === featuredId ? 'none' : ''
    }
    // Attach the stage <video> here too — the FeaturedTile's own
    // onRender/onUpdate don't fire reliably on root-state changes
    // (v3.14), so a screen-share used to un-hide a BLACK stage.
    // attachFeaturedVideo is sid-idempotent, safe on every event.
    if (featured && livekitRoom) {
      const stageVideo = document.querySelector('video[data-featured-tile]')
      if (stageVideo) attachFeaturedVideo(stageVideo, featured, livekitRoom)
    }
  })
}
